import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Users, Calendar, Building2, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { useLang } from '../i18n/LanguageContext';
import { seoFor } from '../data/seoMeta';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const { t, lang } = useLang();
  const seo = seoFor('/contact', lang, { title: 'Contact & quote — France & Dubai | Party LED', description: 'A question, an event over 1,000 people, a custom need? Write to us, reply within 24 h. Offices in France and Dubai.' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    eventType: '',
    guestCount: '',
    eventDate: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${BACKEND_URL}/api/public/quotes`, formData);
      setSubmitted(true);
      toast({
        title: "Demande envoyée !",
        description: "Nous vous répondrons dans les plus brefs délais.",
      });
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible d'envoyer votre demande",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-black pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4 text-center py-20" data-testid="quote-success">
          <div className="bg-gray-900 rounded-2xl p-12 border border-gray-800">
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              {t.contact.successTitle}
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              {t.contact.successMsg}
            </p>
            <Button
              onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', company: '', eventType: '', guestCount: '', eventDate: '', message: '' }); }}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="new-quote-btn"
            >
              {t.contact.newQuote}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href="https://www.party-led.com/contact" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:image" content="https://www.party-led.com/og-image.jpg" />
        <meta property="og:url" content="https://www.party-led.com/contact" />
        <meta property="og:type" content="website" />
      </>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }} data-testid="contact-title">
            {t.contact.title}
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <div className="bg-gray-900 rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-6">{t.contact.info}</h2>
              
              <div className="space-y-6">
                {/* France - Nouvelle Vision */}
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-600 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1"><span className="mr-1.5">&#127467;&#127479;</span> Nouvelle Vision (Europe)</h3>
                    <p className="text-gray-400">8 Rue Saint Savournin</p>
                    <p className="text-gray-400">13001 Marseille, France</p>
                  </div>
                </div>

                {/* UAE - World Promotional Products */}
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-600 p-3 rounded-lg">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1"><span className="mr-1.5">&#127462;&#127466;</span> World Promotional Products (UAE)</h3>
                    <p className="text-gray-400">Opal Tower, Burj Khalifa Boulevard</p>
                    <p className="text-gray-400">Office 307/22, Business Bay</p>
                    <p className="text-gray-400">Dubai, UAE</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-600 p-3 rounded-lg">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Email</h3>
                    <p className="text-gray-400">info@nouvelle-vision.com (Europe)</p>
                    <p className="text-gray-400">info@w-p-p.ae (UAE)</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-600 p-3 rounded-lg">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">{t.contact.phone}</h3>
                    <p className="text-gray-400">+33 4 91 48 12 13 (France)</p>
                    <p className="text-gray-400">+971 58 507 0177 (UAE)</p>
                    <p className="text-gray-400">+971 58 550 0626 (UAE)</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg p-8 text-white">
              <h3 className="text-2xl font-bold mb-4">{t.contact.whyTitle}</h3>
              <ul className="space-y-3">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>{t.contact.why1}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>{t.contact.why2}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>{t.contact.why3}</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                  <span>{t.contact.why4}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Quote Form */}
          <div>
            <div className="bg-gray-900 rounded-lg p-8">
              <h2 className="text-2xl font-bold text-white mb-6">{t.contact.formTitle}</h2>
              
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      {t.contact.name} *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-gray-800 text-white border-gray-700 focus:ring-2 focus:ring-purple-500"
                      placeholder="Votre nom"
                      data-testid="quote-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      {t.contact.emailField} *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-gray-800 text-white border-gray-700 focus:ring-2 focus:ring-purple-500"
                      placeholder="votre@email.com"
                      data-testid="quote-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                      Téléphone
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-gray-800 text-white border-gray-700 focus:ring-2 focus:ring-purple-500"
                      placeholder="+33 6 12 34 56 78"
                      data-testid="quote-phone"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                      Société / Organisation
                    </label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-gray-800 text-white border-gray-700 focus:ring-2 focus:ring-purple-500"
                      placeholder="Nom de la société"
                      data-testid="quote-company"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="eventType" className="block text-sm font-medium text-gray-300 mb-2">
                      {t.contact.eventType}
                    </label>
                    <Select value={formData.eventType} onValueChange={(v) => setFormData({ ...formData, eventType: v })}>
                      <SelectTrigger className="bg-gray-800 text-white border-gray-700" data-testid="quote-event-type">
                        <SelectValue placeholder={t.contact.choose} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mariage">{t.contact.wedding}</SelectItem>
                        <SelectItem value="Festival">{t.contact.festival}</SelectItem>
                        <SelectItem value="Concert">{t.contact.concert}</SelectItem>
                        <SelectItem value="Soirée privée">{t.contact.privateParty}</SelectItem>
                        <SelectItem value="Événement corporate">{t.contact.corporate}</SelectItem>
                        <SelectItem value="Autre">{t.contact.other}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label htmlFor="guestCount" className="block text-sm font-medium text-gray-300 mb-2">
                      {t.contact.guestCount}
                    </label>
                    <Select value={formData.guestCount} onValueChange={(v) => setFormData({ ...formData, guestCount: v })}>
                      <SelectTrigger className="bg-gray-800 text-white border-gray-700" data-testid="quote-guest-count">
                        <SelectValue placeholder={t.contact.choose} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50-100">50 - 100 personnes</SelectItem>
                        <SelectItem value="100-200">100 - 200 personnes</SelectItem>
                        <SelectItem value="200-300">200 - 300 personnes</SelectItem>
                        <SelectItem value="300-500">300 - 500 personnes</SelectItem>
                        <SelectItem value="500-800">500 - 800 personnes</SelectItem>
                        <SelectItem value="800-1000">800 - 1000 personnes</SelectItem>
                        <SelectItem value="1000+">1000+ personnes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <label htmlFor="eventDate" className="block text-sm font-medium text-gray-300 mb-2">
                    {t.contact.eventDate}
                  </label>
                  <Input
                    id="eventDate"
                    name="eventDate"
                    type="date"
                    value={formData.eventDate}
                    onChange={handleChange}
                    className="bg-gray-800 text-white border-gray-700 focus:ring-2 focus:ring-purple-500"
                    data-testid="quote-event-date"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    {t.contact.message} *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-gray-800 text-white border-gray-700 focus:ring-2 focus:ring-purple-500 resize-none"
                    placeholder={t.contact.messagePlaceholder}
                    data-testid="quote-message"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 text-lg font-semibold"
                  data-testid="quote-submit-btn"
                >
                  <Send className="w-5 h-5 mr-2" />
                  {loading ? t.contact.sending : t.contact.submit}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
